import { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { fmtCurrency, DEFAULT_CURRENCY, DEFAULT_SYMBOL } from "@/lib/api";

// Module-level cache — the currency catalog is effectively static, so every
// page that needs it can share a single fetch instead of re-requesting it.
let cache = null;
let inflight = null;

export function useCurrencies() {
  const [currencies, setCurrencies] = useState(cache || []);
  useEffect(() => {
    if (cache) return;
    if (!inflight) inflight = api.get("/currencies").then((r) => (cache = r.data));
    inflight.then((data) => setCurrencies(data)).catch(() => {});
  }, []);
  return currencies;
}

export const currencySymbol = (code, currencies) =>
  currencies.find((c) => c.code === code)?.symbol || (code && code !== DEFAULT_CURRENCY ? code + " " : DEFAULT_SYMBOL);

/** Sum a list of records into per-currency totals (deals, quotes, etc.). */
export const sumByCurrency = (items, valueKey = "value", currencyKey = "currency") => {
  const totals = {};
  for (const item of items) {
    const code = item[currencyKey] || DEFAULT_CURRENCY;
    totals[code] = (totals[code] || 0) + Number(item[valueKey] || 0);
  }
  return totals;
};

/** Sort currency totals by value descending. */
export const sortedCurrencyEntries = (totals) =>
  Object.entries(totals || {}).sort((a, b) => b[1] - a[1]);

/** Collect unique currency codes from multiple maps, sorted by primary map value. */
export const currencyCodesFrom = (primary, ...rest) => {
  const codes = new Set([...Object.keys(primary || {}), ...rest.flatMap((m) => Object.keys(m || {}))]);
  return [...codes].sort((a, b) => (primary?.[b] || 0) - (primary?.[a] || 0));
};

/** Format a { USD: 1000, INR: 500 } map from the stats API. */
export const formatCurrencyTotals = (totals, currencies) => {
  const entries = Object.entries(totals || {});
  if (entries.length === 0)
    return fmtCurrency(0, DEFAULT_CURRENCY, currencySymbol(DEFAULT_CURRENCY, currencies));
  return entries.map(([code, v]) => fmtCurrency(v, code, currencySymbol(code, currencies))).join(" + ");
};
